import classNames from "classnames";
import Link from "next/link";
import * as React from "react";
import { AppLinkSection } from "@components/AppLinkSection";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import { paths } from "@libs/paths";
import { IFImg } from "@libs/utils/IFImg";
import { doScroll } from "@libs/utils/scroll";
import styles from "./index.module.css";

// ハイフンを含まない名前のクオートが外されるのでPrettierを無効化
// prettier-ignore
const BENEFITS = {
  "hub-82": {
    name: "毎回の飲食で5%割引",
    attentions: [
      "お会計から5%OFFの特典は2023年1月31日(火)までとなります。",
      "対象店舗は本ページ記載のHUB・82各店舗になります。（HUB六本木店、HUB浅草店、HUB福岡PayPayドーム店、スタジアム内売店施設、HUBトラックは対象外です。）",
      "DAZNアプリは必ずお会計の前にご提示ください。",
      "一度のお会計の値引き上限額は5,000円となります。",
      "他の割引特典との併用はできません。"
    ]
  },
  "the-public-stand": {
    name: "時間無制限飲み放題が男性1000円引き女性半額",
    attentions: [
      "入店時にスタッフにDAZNアプリを提示してください。",
      "未成年者のご入場は固くお断りしております。",
      "他の特典との併用はできません。",
      "同じ店舗であれば出入りは自由となります。判別のために当日のレシート確認とブラックライトスタンプを押させていただきます。"
    ]
  },
  "red-angel": {
    name: "プチおつまみサービス",
    attentions: [
      "特典のみの受け取りは不可となります。",
      "注文時にスタッフにDAZNアプリを提示してください。",
      "一回のお食事につき、お一人様1日1回限り使用可能です。",
      "他の特典との併用はできません。"
    ]
  },
  "kanpai-terrace": {
    name: "お会計から5%割引 (KANPAI TERRACE)",
    attentions: [
      "DAZNアプリは必ずお会計の前にご提示ください。",
      "他の特典との併用はできません。",
      "お一人様1日1回限り使用可能です。"
    ]
  },
  "livre": {
    name: "お会計から5%割引 (Cafe Bar LIVRE)",
    attentions: [
      "DAZNアプリは必ずお会計の前にご提示ください。",
      "他の特典との併用はできません。",
      "一度のお会計の値引き上限額は1,000円となります。",
      "お一人様1日1回限り使用可能です。"
    ]
  },
  "kennys": {
    name: "おすすめフード1品サービス",
    attentions: [
      "特典のみの受け取りは不可となります。",
      "注文時にスタッフにDAZNアプリを提示してください。",
      "お一人様1日1回限り使用可能です。",
      "他の特典との併用はできません。"
    ]
  },
  "alc-beef-kitchen": {
    name: "等々力ーラ(テキーラ)サービスor飲み放題値引き",
    attentions: [
      "注文時にスタッフにDAZNアプリを提示してください。",
      "特典のみの受け取りは不可となります。",
      "お一人様、あたりいずれかの特典を1日1回限り使用可能です。",
      "全メニュー飲み放題2時間（2,000円）が1,500円になります。"
    ]
  },
  "restore": {
    name: "1名様につきグランドメニュー内のドリンク1杯無料",
    attentions: [
      "注文時にスタッフにDAZNアプリを提示してください。",
      "特典のみの受け取りは不可となります。",
      "お一人様1日1回限り使用可能です。"
    ]
  },
  "anima-rossa": {
    name: "応援ドリンク1杯サービス",
    attentions: [
      "注文時にスタッフにDAZNアプリを提示してください。",
      "特典のみの受け取りは不可となります。",
      "お一人様1日1回限り使用可能です。",
      "他の特典との併用はできません。"
    ]
  },
  "kakushin": {
    name: '"名物秘伝のからあげ"ご注文の方にソーストッピング無料',
    attentions: [
      "特典のみの受け取りは不可となります。",
      "注文時にスタッフにDAZNアプリを提示してください。",
      "お一人様1日1回限り使用可能です。",
      "他の特典との併用はできません。"
    ]
  },
  "sakuraholic": {
    name: "初めてご来店の方限定でミックスナッツプレゼント",
    attentions: [
      "特典のみの受け取りは不可となります。",
      "注文時にスタッフにDAZNアプリを提示してください。",
      "お一人様1回限り使用可能です。",
      "初めてご来店の方限定となります。"
    ]
  },
  "oru": {
    name: "ドリンク1杯サービス",
    attentions: [
      "特典のみの受け取りは不可となります。",
      "注文時にスタッフにDAZNアプリを提示してください。",
      "一回のお食事につき、お一人様1日1回限り使用可能です。",
      "他の特典との併用はできません。",
      "お選びいただけるドリンクには制限がございます。詳しくはスタッフまでお声がけください。"
    ]
  }
} as const;

type BenefitName = keyof typeof BENEFITS;

// 注意事項を表示する順番
const ATTENTION_ORDER: BenefitName[] = [
  "hub-82",
  "the-public-stand",
  "red-angel",
  "kanpai-terrace",
  "livre",
  "kennys",
  "alc-beef-kitchen",
  "restore",
  "anima-rossa",
  "kakushin",
  "sakuraholic",
  "oru",
];

const DaznMemberBenefits202208: React.FC = () => {
  const attention = (benefitName: BenefitName, index: number) => {
    const { name, attentions } = BENEFITS[benefitName];
    return (
      <dl
        id={`attention-${benefitName}`}
        className={styles.attentionItems}
        key={index}
      >
        <dt className={styles.attentionItemsTitle}>{name}</dt>
        <dd className={styles.attentionItemsContent}>
          <ul>
            {attentions.map((text, index) => (
              <li key={index}>{text}</li>
            ))}
          </ul>
        </dd>
      </dl>
    );
  };

  // スムーススクロール
  const onClickSmooth = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const href = event.currentTarget.attributes.getNamedItem("href");

    if (href) {
      const hash = href?.nodeValue;

      if (hash) {
        event.preventDefault();

        const target = document.querySelector(hash);
        doScroll(target);

        // For updating hash in the address bar.
        history.pushState(null, "", hash);
      }
    }
  };

  return (
    <CommonLayout hasFooter noModal>
      <CommonHead
        title={"お店でトクするDAZN！飲食店でもお得な特典が！"}
        description={
          "DAZN会員ならFansta掲載の対象店舗でおトクな特典を受けられます。お店でDAZNアプリを提示するだけ。期間は2022年8月3日から2023年1月31日まで。お店でトクするDAZN！"
        }
        ogUrl={paths.campaignDaznMemberBenefits202208}
        ogImage={`${process.env.APP_HOST}/img/campaign/dazn-member-benefits-202208/ogp.jpg`}
        canonicalUrl={paths.campaignDaznMemberBenefits202208}
      />

      <div className={styles.wrapper}>
        <div className={styles.closeLabel}>
          <span>※キャンペーンは終了しました</span>
        </div>
        <div className={styles.jumbotron}>
          <h1 className={styles.jumbotronMv}>
            <span className="md:none">
              <img
                src={IFImg(
                  "/img/campaign/dazn-member-benefits-202208/sp/jumbotron_main.svg"
                )}
                width={375}
                height={454}
                alt="お店でトクするDAZN"
              />
            </span>
            <span className="none md:block">
              <img
                src={IFImg(
                  "/img/campaign/dazn-member-benefits-202208/pc/jumbotron_main.svg"
                )}
                width={600}
                height={716}
                alt="お店でトクするDAZN"
              />
            </span>
          </h1>
          <div className={styles.date}>
            <img
              src={IFImg("/img/campaign/dazn-member-benefits-202208/date.svg")}
              width={310}
              height={86}
              alt="キャンペーン期間 2022年8月3日〜2023年1月31日"
            />
          </div>
        </div>

        <section className={styles.about}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>
              <img
                src={IFImg(
                  "/img/campaign/dazn-member-benefits-202208/about_title.png"
                )}
                width={274}
                height={44}
                alt="お店でトクするDAZN キャンペーン概要"
              />
            </h2>
            <div className={styles.aboutText}>
              対象店舗でDAZNアプリを見せると
              <em>毎回の飲食で5%割引やドリンク・フードのサービス</em>
              などお得な特典が！
            </div>
            <div className={styles.sectionButton}>
              <a href="#how-to-receive-benefits" onClick={onClickSmooth}>
                <span>特典の受け方はこちら</span>
              </a>
            </div>
          </div>
        </section>

        <section className={styles.howto} id={`how-to-receive-benefits`}>
          <div className={styles.container}>
            <div className={styles.howtoInner}>
              <h2 className={styles.sectionTitle}>
                <img
                  src={IFImg(
                    "/img/campaign/dazn-member-benefits-202208/howto_title.png"
                  )}
                  width={230}
                  height={44}
                  alt="お店でトクするDAZN 特典の受け方"
                />
              </h2>

              <div>
                <div className={styles.howtoBox}>
                  <div
                    className={classNames(styles.detailImage, "none md:block")}
                  >
                    <img
                      src={IFImg(
                        "/img/campaign/dazn-member-benefits-202208/howto_img_001.png"
                      )}
                      width={1051}
                      height={700}
                      alt="お店でトクするDAZN DAZNロゴ"
                    />
                  </div>
                  <div className={styles.detail}>
                    <h3 className={styles.detailTitle}>
                      <span className={styles.count}>01</span>
                      DAZNアプリをインストール&会員登録
                    </h3>
                    <div className={classNames(styles.detailImage, "md:none")}>
                      <img
                        src={IFImg(
                          "/img/campaign/dazn-member-benefits-202208/howto_img_001.png"
                        )}
                        width={1051}
                        height={700}
                        alt="お店でトクするDAZN DAZNロゴ"
                      />
                    </div>
                    <div className={styles.detailText}>
                      スマートフォンでDAZNアプリをインストールして会員登録を行ってください。
                    </div>
                    <div className={styles.detailDownload}>
                      <p className={styles.title}>
                        <img
                          className={styles.mark}
                          src={IFImg(
                            "/img/campaign/dazn-member-benefits-202208/right-triangle.svg"
                          )}
                          alt=""
                        />
                        DAZNアプリダウンロード
                      </p>
                      <ul className={styles.button}>
                        <li className={styles.ios}>
                          <a
                            href="https://apps.apple.com/jp/app/dazn/id1129523589"
                            target="_blank"
                          >
                            <img
                              src={IFImg(
                                "/img/campaign/dazn-member-benefits-202208/howto_download_ios.svg"
                              )}
                              width={110}
                              height={41}
                              alt=""
                            />
                          </a>
                        </li>
                        <li className={styles.google}>
                          <a
                            href="https://play.google.com/store/apps/details?id=com.dazn&hl=ja"
                            target="_blank"
                          >
                            <img
                              src={IFImg(
                                "/img/campaign/dazn-member-benefits-202208/howto_download_google.svg"
                              )}
                              width={136}
                              height={41}
                              alt=""
                            />
                          </a>
                        </li>
                        <li className={styles.amazon}>
                          <a
                            href="https://www.amazon.co.jp/DAZN/dp/B01IPC7MBE"
                            target="_blank"
                          >
                            <img
                              src={IFImg(
                                "/img/campaign/dazn-member-benefits-202208/howto_download_amazon.svg"
                              )}
                              width={138}
                              height={41}
                              alt=""
                            />
                          </a>
                        </li>
                      </ul>
                    </div>
                    <div className={styles.detailLink}>
                      <a
                        href="https://www.dazn.com/ja-JP/help/articles/how-do-i-register-jp"
                        target="_blank"
                      >
                        DAZN会員登録方法
                      </a>
                    </div>
                  </div>
                </div>

                <div className={styles.howtoBox}>
                  <div
                    className={classNames(styles.detailImage, "none md:block")}
                  >
                    <img
                      src={IFImg(
                        "/img/campaign/dazn-member-benefits-202208/howto_img_002.png"
                      )}
                      width={1051}
                      height={700}
                      alt="お店でトクするDAZN DAZNアプリのメニュー画面"
                    />
                  </div>
                  <div className={styles.detail}>
                    <h3 className={styles.detailTitle}>
                      <span className={styles.count}>02</span>
                      DAZNアプリのメニュー画面を表示
                    </h3>
                    <div className={classNames(styles.detailImage, "md:none")}>
                      <img
                        src={IFImg(
                          "/img/campaign/dazn-member-benefits-202208/howto_img_002.png"
                        )}
                        width={1051}
                        height={700}
                        alt="お店でトクするDAZN DAZNアプリのメニュー画面"
                      />
                    </div>
                    <div className={styles.detailText}>
                      アプリにログインして、iPhone端末は右下の「その他」から、Android端末は左上の「メニューアイコン」から表示できます。
                    </div>
                  </div>
                </div>

                <div className={styles.howtoBox}>
                  <div
                    className={classNames(styles.detailImage, "none md:block")}
                  >
                    <img
                      src={IFImg(
                        "/img/campaign/dazn-member-benefits-202208/howto_img_003.png"
                      )}
                      width={1051}
                      height={700}
                      alt="お店でトクするDAZN 提示しておトク"
                    />
                  </div>
                  <div className={styles.detail}>
                    <h3 className={styles.detailTitle}>
                      <span className={styles.count}>03</span>
                      お会計時にスタッフに提示しておトク
                    </h3>
                    <div className={classNames(styles.detailImage, "md:none")}>
                      <img
                        src={IFImg(
                          "/img/campaign/dazn-member-benefits-202208/howto_img_003.png"
                        )}
                        width={1051}
                        height={700}
                        alt="お店でトクするDAZN 土曜日はお会計から10%OFF"
                      />
                    </div>
                    <div className={styles.detailText}>
                      お会計時にDAZNアプリのメニュー画面をスタッフに提示すると、お得な特典を受けることができます。
                    </div>
                    <div className={styles.detailNote}>
                      <ul>
                        <li>
                          ※店舗によって特典が異なります。本ページ記載の各店舗の特典内容をご確認ください。
                        </li>
                        <li>※DAZNアプリはご自身のものに限り有効です。</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.sectionButton}>
                <a href="#shops" onClick={onClickSmooth}>
                  <span>対象店舗はこちら</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className={styles.attention}>
          <div className={styles.container}>
            <div className={styles.attentionBlock}>
              <h2
                className={classNames(styles.sectionTitle, styles.attentionCP)}
              >
                <img
                  src={IFImg(
                    "/img/campaign/dazn-member-benefits-202208/cp_attention_title.png"
                  )}
                  width={340}
                  height={44}
                  alt="お店でトクするDAZN キャンペーンの注意事項"
                />
              </h2>

              <div className={styles.attentionBox}>
                <div className={styles.attentionInner}>
                  <ul>
                    <li>
                      キャンペーン期間は2022年8月3日から2023年1月31日までとなります。
                    </li>
                    <li>
                      対象店舗や特典は予告なく変更・終了する場合がございます。
                    </li>
                    <li>
                      本ページ記載の割引特典を受ける場合は、必ず以下の注意事項をお読みください。
                    </li>
                    <li>
                      特典に関するお問い合わせは、各対象店舗までご連絡ください。
                    </li>
                    <li>
                      特典の対象者はDAZN会員に限り、提示されるDAZNアプリはご自身のものに限り有効です。
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className={styles.attentionBlock}>
              <h2
                className={classNames(
                  styles.sectionTitle,
                  styles.attentionTokuten
                )}
              >
                <img
                  src={IFImg(
                    "/img/campaign/dazn-member-benefits-202208/tokuten_attention_title.png"
                  )}
                  width={252}
                  height={44}
                  alt="お店でトクするDAZN 特典の注意事項"
                />
              </h2>

              <div className={styles.attentionBox}>
                <div className={styles.attentionInner}>
                  {ATTENTION_ORDER.map(attention)}
                </div>
              </div>
            </div>

            <div className={classNames(styles.sectionButton, styles.white)}>
              <a href="#shops" onClick={onClickSmooth}>
                <span>対象店舗はこちら</span>
              </a>
            </div>
          </div>
        </section>

        <div className={styles.contact}>
          <div className={styles.container}>
            <div className={styles.contactButton}>
              本キャンペーンについてのお問い合わせは、
              <Link href="https://support.fansta.jp/hc/ja/requests/new">
                Fanstaお問い合わせ窓口
              </Link>
              までお願いいたします。
            </div>
          </div>
        </div>

        <AppLinkSection customStyle={styles.appLinkSection} />

        <section className={styles.fanstaShop}>
          <div className={styles.container}>
            <h2 className={styles.fanstaShopTitle}>
              <span>
                <em>掲載をご希望のお店の方へ</em>
              </span>
            </h2>

            <div className={styles.fanstaShopCampaign}>
              <span className={"md:none"}>
                <img
                  src={IFImg(
                    "/img/campaign/dazn-member-benefits-202208/sp/publish_campaign.svg"
                  )}
                  width="319"
                  alt="今ならお得なキャンペーン実施中!! 月額料金(税抜)¥10,000→無料(2023/3/31まで！) "
                  className={styles.fanstaShopCampaignTextImage}
                />
              </span>

              <span className={"none md:block"}>
                <img
                  src={IFImg(
                    "/img/campaign/dazn-member-benefits-202208/pc/publish_campaign.svg"
                  )}
                  width="480"
                  alt="今ならお得なキャンペーン実施中!! 月額料金(税抜)¥10,000→無料(2023/3/31まで！) "
                  className={styles.fanstaShopCampaignTextImage}
                />
              </span>

              <div className={styles.fanstaShopCampaignText}>
                ※別途初期費用
                ¥30,000(税抜)ネット予約手数料¥200(税抜)×来店人数がかかります。
              </div>
            </div>

            <p className={styles.fanstaShopText}>
              Fanstaへの掲載・資料請求をご希望のお店の方は以下からお問い合わせください。
            </p>

            <div>
              <Link
                href={paths.contact}
                className={styles.fanstaShopButton}
                id="contact-link-dazn-member-benefits-202208"
              >
                <span>掲載のお問い合わせはこちら</span>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </CommonLayout>
  );
};

export default DaznMemberBenefits202208;
